import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, FileText, ArrowRight, CheckCircle2, CreditCard, Receipt, ExternalLink } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/customerApi";
import { QuoteCheckout } from '@/components/QuoteCheckout';
import { AccountNotifications } from '@/components/StockNotifications';
import { AccountDeliveries } from '@/components/account/AccountDeliveries';
import Seo from "@/components/Seo";

export default function AccountDashboard() {
  const { customer, logout } = useCustomerAuth();
  const nav = useNavigate();
  const [enquiries, setEnquiries] = useState(null);
  const [orders, setOrders] = useState(null);
  const [kits, setKits] = useState(null);

  useEffect(() => {
    if (!customer) return;
    customerApi.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    customerApi.get("/account/kit-builds").then(({ data }) => setKits(data)).catch(() => setKits([]));
  }, [customer]);

  useEffect(() => {
    if (customer === false) {
      nav("/account/login");
      return;
    }
    if (customer) {
      customerApi
        .get("/account/enquiries")
        .then(({ data }) => setEnquiries(data))
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            logout();
            nav("/account/login");
          } else {
            setEnquiries([]);
          }
        });
    }
  }, [customer, nav, logout]);

  if (customer === null) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ei-navy" />
      </div>
    );
  }
  if (!customer) return null;

  return (
    <>
      <Seo title="My trade account — EI Battery Solutions" description="Your EI Battery trade account dashboard." />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-ei-navy" data-testid="account-welcome">Welcome, {customer.name}</h1>
            <p className="mt-2 text-sm text-ei-graphite/75">{customer.company} · {customer.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="account-logout"
            className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/request-a-quote"
            data-testid="account-new-quote"
            className="font-display text-sm font-bold bg-ei-gold text-ei-navy px-6 py-3.5 rounded-sm hover:bg-[#b89747] inline-flex items-center gap-2 min-h-[44px]"
          >
            Request a quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <AccountNotifications />
        <QuoteCheckout />
        <h2 className="mt-12 text-xl font-semibold text-ei-navy">Your enquiries</h2>
        <div className="mt-4" data-testid="account-enquiries">
          {enquiries === null ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ei-navy" /></div>
          ) : enquiries.length === 0 ? (
            <p className="text-sm text-ei-graphite/60">
              No enquiries yet. Use “Request a quote” to submit your first one — make sure to use this account email ({customer.email}).
            </p>
          ) : (
            <div className="space-y-3">
              {enquiries.map((e) => (
                <div
                  key={e.id}
                  className="border border-ei-mist rounded-md p-4 bg-white flex items-center justify-between gap-4"
                  data-testid={`account-enquiry-${e.id}`}
                >
                  <div>
                    <div className="font-semibold text-ei-navy flex items-center gap-2">
                      <FileText className="w-4 h-4 text-ei-golddark" /> {e.reference}
                    </div>
                    <div className="mt-1 text-xs text-ei-graphite/60">
                      {(e.products || []).map((p) => `${p.quantity}× ${p.name}`).join(", ")}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs font-semibold text-ei-navy bg-ei-mist/50 px-3 py-1 rounded-full">{e.stage}</span>
                    {e.quote?.payment?.status === 'legacy_review_required' && <span className="text-xs text-amber-800" data-testid={`legacy-review-${e.id}`}>Historical payment record — staff review required</span>}
                    {e.quote?.payment?.status === "paid" && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full" data-testid={`account-paid-${e.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Paid · Order {e.quote.payment.order_name}
                      </span>
                    )}
                    {e.quote?.payment?.status === "awaiting" && (
                      <a href={e.quote.payment.pay_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold bg-ei-gold text-ei-navy px-3 py-1.5 rounded-sm hover:bg-[#b89747] transition-colors" data-testid={`account-pay-${e.id}`}>
                        <CreditCard className="w-3.5 h-3.5" /> Pay now
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className="mt-12 text-xl font-semibold text-ei-navy">Your kit builds</h2>
        <div className="mt-4" data-testid="account-kits">
          {kits === null ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ei-navy" /></div>
          ) : kits.length === 0 ? (
            <p className="text-sm text-ei-graphite/60">No kit builds yet. Use <Link to="/build-your-kit" className="underline text-ei-navy">Build a kit</Link> to configure a system and our team will review it.</p>
          ) : (
            <div className="space-y-3">
              {kits.map((k) => {
                const tone = k.paid_at ? "bg-emerald-50 text-emerald-800 border-emerald-200" : k.status === "Approved" || k.status === "Draft order sent" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : k.status === "Needs changes" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-ei-mist/50 text-ei-navy border-ei-mist";
                return (
                  <div key={k.id} className="border border-ei-mist rounded-md p-4 bg-white" data-testid={`account-kit-${k.reference}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-ei-navy">{k.kit_label}</div>
                        <div className="mt-1 text-xs text-ei-graphite/60 tnum">{k.reference} · {k.phase}-phase · {k.battery_kwh} kWh battery · {k.solar_kw} kW solar · {k.inverter}</div>
                        <div className="mt-2 text-xs text-ei-graphite/70">{k.lines.map((l) => `${l.qty}× ${l.name}`).join(" · ")}</div>
                        {k.review_notes && <div className="mt-2 text-sm text-ei-graphite/85 bg-ei-mist/30 rounded-sm px-3 py-2" data-testid={`account-kit-notes-${k.reference}`}><span className="font-semibold text-ei-navy">Technical review:</span> {k.review_notes}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${tone}`} data-testid={`account-kit-status-${k.reference}`}>{k.paid_at ? `Paid · Order ${k.order_name}` : k.status}{k.adjusted && !k.paid_at ? " (adjusted)" : ""}</span>
                        <span className="font-display font-bold text-ei-navy tnum">${Number(k.total_inc_gst || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })} <span className="text-xs font-normal text-ei-graphite/60">inc GST</span></span>
                        {k.pay_url && <a href={k.pay_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold bg-ei-gold text-ei-navy px-3 py-1.5 rounded-sm hover:bg-[#b89747] transition-colors" data-testid={`account-kit-pay-${k.reference}`}><CreditCard className="w-3.5 h-3.5" /> Pay now</a>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <h2 className="mt-12 text-xl font-semibold text-ei-navy">Deliveries</h2>
        <AccountDeliveries />

        <h2 className="mt-12 text-xl font-semibold text-ei-navy">Orders & invoices</h2>
        <div className="mt-4" data-testid="account-orders">
          {orders === null ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ei-navy" /></div>
          ) : orders.length === 0 ? (
            <p className="text-sm text-ei-graphite/60">No orders yet. Once a quote or kit is sent to payment it will appear here with its Shopify order and Xero invoice.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.kind + o.reference} className="border border-ei-mist rounded-md p-4 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`account-order-${o.reference}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-ei-navy flex items-center gap-2"><Receipt className="w-4 h-4 text-ei-golddark" /> {o.label}</div>
                    <div className="mt-1 text-xs text-ei-graphite/60 tnum">
                      {o.kind === "kit" ? "Kit build" : "Quote"} {o.reference}
                      {o.legacy_order_name && <> · order {o.legacy_order_name}</>}
                      {o.xero_invoice && <> · Invoice {o.xero_invoice}{o.xero_status && ` (${o.xero_status.toLowerCase()})`}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-display font-bold text-ei-navy tnum">${Number(o.total || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</span>
                    {o.legacy_payment_review && <span className="text-xs text-amber-800" data-testid={`legacy-order-${o.reference}`}>Historical record — payment unverified</span>}
                    {o.paid_at ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Paid {new Date(o.paid_at).toLocaleDateString("en-AU")}</span>
                    ) : o.pay_url ? (
                      <a href={o.pay_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold bg-ei-gold text-ei-navy px-3 py-1.5 rounded-sm hover:bg-[#b89747] transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Pay now</a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
